import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowLeft, Pause, Play, RotateCcw } from "lucide-react";

const BubblePopGame = ({ onBack }) => {
  const [bubbles, setBubbles] = useState([]);
  const [score, setScore] = useState(0);
  const [isActive, setIsActive] = useState(false);
  const [breathingPhase, setBreathingPhase] = useState("inhale");
  const [breathingCount, setBreathingCount] = useState(4);

  const generateBubble = () => {
    return {
      id: Math.random(),
      x: Math.random() * 80 + 10, // 10-90% from left
      y: Math.random() * 80 + 10, // 10-90% from top
      size: Math.random() * 40 + 20, // 20-60px
      color: `hsl(${Math.random() * 360}, 70%, 80%)`,
      opacity: 0.8,
    };
  };

  useEffect(() => {
    if (isActive) {
      const interval = setInterval(() => {
        setBubbles((prev) => {
          if (prev.length < 8) {
            return [...prev, generateBubble()];
          }
          return prev;
        });
      }, 2000);
      return () => clearInterval(interval);
    }
  }, [isActive]);

  useEffect(() => {
    if (isActive) {
      const interval = setInterval(() => {
        setBreathingCount((prev) => {
          if (prev > 1) {
            return prev - 1;
          } else {
            const phases = ["inhale", "hold", "exhale", "hold"];
            const currentIndex = phases.indexOf(breathingPhase);
            const nextPhase = phases[(currentIndex + 1) % phases.length];
            setBreathingPhase(nextPhase);
            return 4;
          }
        });
      }, 1000);
      return () => clearInterval(interval);
    }
  }, [isActive, breathingPhase]);

  const popBubble = (bubbleId) => {
    setBubbles((prev) => prev.filter((bubble) => bubble.id !== bubbleId));
    setScore((prev) => prev + 1);
  };

  const startGame = () => {
    setIsActive(true);
    setBubbles([generateBubble(), generateBubble(), generateBubble()]);
  };

  const pauseGame = () => {
    setIsActive(!isActive);
  };

  const resetGame = () => {
    setIsActive(false);
    setBubbles([]);
    setScore(0);
    setBreathingPhase("inhale");
    setBreathingCount(4);
  };

  return (
    <div className="min-h-screen p-4">
      {/* Header */}
      <div className="flex items-center justify-between mb-4 sm:mb-6">
        <button
          onClick={onBack}
          className="flex items-center gap-1 sm:gap-2 px-2 sm:px-4 py-2 bg-white/80 rounded-xl hover:bg-white transition-colors"
        >
          <ArrowLeft className="w-4 h-4 sm:w-5 sm:h-5" />
          <span className="text-sm sm:text-base">Back</span>
        </button>

        <h1 className="text-lg sm:text-2xl font-bold text-gray-800 text-center flex-1 mx-2">
          <span className="hidden sm:inline">🫧 Bubble Pop Meditation</span>
          <span className="sm:hidden">🫧 Bubble Pop</span>
        </h1>

        <div className="flex items-center gap-2">
          <span className="bg-white/80 px-2 sm:px-3 py-1 rounded-full text-xs sm:text-sm font-medium">
            Score: {score}
          </span>
        </div>
      </div>

      {/* Breathing Guide */}
      <div className="text-center mb-6">
        <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-6 max-w-md mx-auto">
          <h3 className="text-lg font-semibold mb-4">Breathing Guide</h3>
          <div className="relative">
            <motion.div
              className="w-24 h-24 mx-auto rounded-full bg-gradient-to-r from-blue-400 to-purple-400 flex items-center justify-center text-white font-bold text-xl"
              animate={{
                scale:
                  breathingPhase === "inhale"
                    ? 1.2
                    : breathingPhase === "exhale"
                    ? 0.8
                    : 1,
              }}
              transition={{ duration: 1 }}
            >
              {breathingCount}
            </motion.div>
            <p className="mt-4 text-lg font-medium capitalize">
              {breathingPhase}
            </p>
            <p className="text-sm text-gray-600 mt-2">
              Pop bubbles while following your breath rhythm
            </p>
          </div>
        </div>
      </div>

      {/* Game Controls */}
      <div className="flex flex-wrap justify-center gap-2 sm:gap-4 mb-4 sm:mb-6 px-2">
        {!isActive && bubbles.length === 0 && (
          <button
            onClick={startGame}
            className="bg-green-500 hover:bg-green-600 text-white px-4 sm:px-6 py-2 sm:py-3 rounded-xl font-medium flex items-center gap-2 text-sm sm:text-base"
          >
            <Play className="w-4 h-4 sm:w-5 sm:h-5" />
            <span className="hidden sm:inline">Start Meditation</span>
            <span className="sm:hidden">Start</span>
          </button>
        )}

        {bubbles.length > 0 && (
          <>
            <button
              onClick={pauseGame}
              className="bg-blue-500 hover:bg-blue-600 text-white px-4 sm:px-6 py-2 sm:py-3 rounded-xl font-medium flex items-center gap-2 text-sm sm:text-base"
            >
              {isActive ? (
                <Pause className="w-4 h-4 sm:w-5 sm:h-5" />
              ) : (
                <Play className="w-4 h-4 sm:w-5 sm:h-5" />
              )}
              {isActive ? "Pause" : "Resume"}
            </button>

            <button
              onClick={resetGame}
              className="bg-red-500 hover:bg-red-600 text-white px-4 sm:px-6 py-2 sm:py-3 rounded-xl font-medium flex items-center gap-2 text-sm sm:text-base"
            >
              <RotateCcw className="w-4 h-4 sm:w-5 sm:h-5" />
              Reset
            </button>
          </>
        )}
      </div>

      <div className="relative bg-white/20 backdrop-blur-sm rounded-3xl h-96 mx-auto max-w-4xl overflow-hidden">
        <AnimatePresence>
          {bubbles.map((bubble) => (
            <motion.div
              key={bubble.id}
              initial={{ scale: 0, opacity: 0 }}
              animate={{ scale: 1, opacity: bubble.opacity }}
              exit={{ scale: 0, opacity: 0 }}
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
              className="absolute cursor-pointer select-none"
              style={{
                left: `${bubble.x}%`,
                top: `${bubble.y}%`,
                width: `${bubble.size}px`,
                height: `${bubble.size}px`,
                backgroundColor: bubble.color,
                borderRadius: "50%",
                boxShadow:
                  "inset -5px -5px 10px rgba(0,0,0,0.1), 0 5px 15px rgba(0,0,0,0.1)",
              }}
              onClick={() => popBubble(bubble.id)}
            >
              <div
                className="absolute top-2 left-2 w-3 h-3 bg-white/30 rounded-full"
                style={{ filter: "blur(1px)" }}
              />
            </motion.div>
          ))}
        </AnimatePresence>

        {bubbles.length === 0 && !isActive && (
          <div className="absolute inset-0 flex items-center justify-center">
            <p className="text-gray-500 text-lg">
              Click "Start Meditation" to begin
            </p>
          </div>
        )}
      </div>

      <div className="mt-6 bg-white/80 backdrop-blur-sm rounded-2xl p-4 max-w-2xl mx-auto">
        <h3 className="font-semibold mb-2">How to Play:</h3>
        <ul className="text-sm text-gray-700 space-y-1">
          <li>• Follow the breathing guide - inhale, hold, exhale, hold</li>
          <li>• Gently pop the bubbles as they appear</li>
          <li>• Focus on your breath rhythm while playing</li>
          <li>• Let the game help you relax and be present</li>
        </ul>
      </div>
    </div>
  );
};

export default BubblePopGame;
